/**
 * Générateur — Il calcule des effectifs et des fréquences.
 * Attendu ID : 215 | Niveau : 3ème | Domaine : Données (d14, O3)
 *
 * Source doc prof (OGD FONCTIONS 3e, Créé n°5 de la banque : « de
 * l'histogramme aux fréquences, et retour »). Plan de cartes auto-validé
 * 2026-07-16 (journal TODO_EXERCISEUR_3E.md §6) :
 *
 *   « frequences » — de l'histogramme aux fréquences en % (trous)
 *   « effectifs »  — des fréquences aux effectifs (le retour, trous)
 *   « etendue »    — [v2 2026-07-26, campagne famille C : l'ancienne question
 *                    métacognitive « peut-on connaître l'étendue exacte ? »
 *                    (aucun effectif ni fréquence calculés — hors attendu 215)
 *                    est remplacée par un QCM CALCULÉ en deux variantes :
 *                    fréquence d'une classe/catégorie (supports : tableau,
 *                    bâtons, circulaire, histogramme) et effectif manquant
 *                    retrouvé par soustraction (supports : tableau à cellule
 *                    « ? », histogramme à barre effacée).
 *                    L'ancien contenu, conceptuellement bon, est en réserve
 *                    pour re-rattachement à l'attendu 214 (étendue).]
 *   « journal »    — le titre du journal est-il exact ? (QCM calculé)
 *
 * Pourcentages ENTIERS par construction (effectifs = total × pct / 100).
 */

import {
    randomChoice, randInt,
    makeQcm, makeTexteTrou,
} from '../../../utils';
import { imgElement, svgBarres, svgCamembert, tableEnonce, ligneTable } from '../../5eme/statistiques/svgStats';
import { svgHisto, somme } from './donnees';

const ATTENDU_ID = 215;

const L_FREQUENCES = 'Passer des effectifs aux fréquences';
const L_EFFECTIFS = 'Passer des fréquences aux effectifs';
const L_ETENDUE = 'Fréquence d\'une classe, effectif manquant (QCM)';
const L_JOURNAL = 'Vérifier l\'affirmation d\'un journal';

const opts = (label) => ({ niveau: '3eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const CONTEXTES = [
    { sujet: 'le temps d\'écran quotidien de X élèves de 3e', unite: 'h', amp: 1, lo0: 0, yStep: 10 },
    { sujet: 'le temps de trajet de X collégiens', unite: 'min', amp: 10, lo0: 0, yStep: 10 },
    { sujet: 'la taille de X plants de tomates', unite: 'cm', amp: 20, lo0: 20, yStep: 10 },
];

// Pourcentages entiers ≥ 5 sommant à 100 (multiples de 5).
const tirerPcts = (nb) => {
    for (let essai = 0; essai < 60; essai++) {
        const parts = [];
        let reste = 20;                                    // en « cinq pourcents »
        for (let i = 0; i < nb - 1; i++) {
            const p = randInt(1, Math.max(1, Math.min(reste - (nb - 1 - i), 7)));
            parts.push(p); reste -= p;
        }
        parts.push(reste);
        if (parts.every(p => p >= 1 && p <= 8)) return parts.map(p => p * 5);
    }
    return [10, 25, 30, 20, 10, 5].slice(0, nb);
};

const libClasse = (ctx, i) => `[${ctx.lo0 + i * ctx.amp} ; ${ctx.lo0 + (i + 1) * ctx.amp}[`;

// ═════════ « frequences » — effectifs → % ═════════

const genFrequencesTrous = () => {
    const ctx = randomChoice(CONTEXTES);
    const total = randomChoice([100, 200, 300]);
    const pcts = tirerPcts(6);
    const eff = pcts.map(p => (total * p) / 100);
    const iAsk = [0, randInt(1, 3), randInt(4, 5)];
    const phrase = iAsk.map(i => `${libClasse(ctx, i)} : {{${pcts[i]}}} %`).join(' ; ') + '.';
    const payload = makeTexteTrou(phrase,
        `<p>L'histogramme représente ${ctx.sujet.replace('X', String(total))}.</p>`
        + '<p><b>Calcule la fréquence, en pourcentage, des classes demandées.</b></p>',
        { ...opts(L_FREQUENCES), mode: 'saisie' });
    payload.elements_additionnels = imgElement(svgHisto(ctx.lo0, ctx.amp, eff, { yStep: ctx.yStep, xLabel: ctx.unite }));
    payload._meta.params = { total, pcts, eff, iAsk };
    return payload;
};

// ═════════ « effectifs » — % → effectifs (le retour) ═════════

const genEffectifsTrous = () => {
    const ctx = randomChoice(CONTEXTES);
    const total = randomChoice([120, 160, 200, 240, 300]);
    let pcts;
    for (let essai = 0; essai < 60; essai++) {
        pcts = tirerPcts(5);
        if (pcts.every(p => (total * p) % 100 === 0)) break;
        pcts = null;
    }
    if (!pcts) { pcts = [10, 25, 30, 20, 15]; }
    const eff = pcts.map(p => (total * p) / 100);
    const iAsk = [randInt(0, 1), randInt(2, 3), 4];
    const phrase = iAsk.map(i => `${libClasse(ctx, i)} : {{${eff[i]}}}`).join(' ; ') + '.';
    const payload = makeTexteTrou(phrase,
        `<p>On a étudié ${ctx.sujet.replace('X', String(total))}. Le tableau donne les fréquences :</p>`
        + tableEnonce(
            ligneTable('Classe', pcts.map((_, i) => libClasse(ctx, i))),
            ligneTable('Fréquence', pcts.map(p => `${p} %`)),
        )
        + '<p><b>Calcule les effectifs des classes demandées.</b></p>',
        { ...opts(L_EFFECTIFS), mode: 'saisie' });
    payload._meta.params = { total, pcts, eff, iAsk };
    return payload;
};

// ═════════ « etendue » — fréquence / effectif manquant, 4 supports (QCM, v2) ═════════
// (La clé de format `etendue_qcm_unique` est conservée pour la stabilité du
// registre et des cartes ; le contenu calcule désormais effectifs et fréquences.)
// Supports : variante « frequence » sur tableau, bâtons, circulaire, histogramme ;
// variante « manquant » sur tableau (cellule « ? ») et histogramme (barre effacée)
// — cacher une barre de bâtons ou un secteur serait ambigu. Figures
// auto-descriptives (<desc>, patron des chantiers 4e).

// Séries à CATÉGORIES (bâtons, circulaire) — libellés courts, sans virgule.
const CONTEXTES_CAT = [
    { sujet: 'le sport pratiqué par X licenciés d\'un club', qMot: 'du sport', cats: ['Foot', 'Basket', 'Hand', 'Natation', 'Judo', 'Autre'] },
    { sujet: 'le moyen de transport de X élèves', qMot: 'du moyen', cats: ['À pied', 'Vélo', 'Bus', 'Voiture', 'Trottinette'] },
    { sujet: 'le parfum choisi par X clients d\'un glacier', qMot: 'du parfum', cats: ['Vanille', 'Chocolat', 'Fraise', 'Pistache', 'Citron', 'Mangue'] },
];

const injecteDesc215 = (svg, desc) => svg.replace(/(<svg[^>]*>)/, `$1<desc>${desc}</desc>`);

const genCalculQcm = () => {
    const variante = randomChoice(['frequence', 'manquant']);
    if (variante === 'frequence') {
        const support = randomChoice(['tableau', 'batons', 'circulaire', 'histo']);
        const discret = support === 'batons' || support === 'circulaire';
        const ctx = discret ? randomChoice(CONTEXTES_CAT) : randomChoice(CONTEXTES);
        const nbCl = discret ? ctx.cats.length : 6;
        // total ≠ 100 sinon le distracteur « effectif recopié » = la bonne réponse.
        let total; let pcts; let eff; let i; let j; let ok = false;
        for (let essai = 0; essai < 80 && !ok; essai++) {
            const t = randomChoice([200, 300]);   // const d'itération (no-loop-func)
            total = t;
            pcts = tirerPcts(nbCl);
            eff = pcts.map(p => (t * p) / 100);
            i = randInt(0, nbCl - 1);
            j = randInt(0, nbCl - 1);
            ok = pcts[i] !== 50                            // complément ≠ bonne réponse
                && j !== i && pcts[j] !== pcts[i] && pcts[j] !== 100 - pcts[i]
                && eff[i] !== pcts[i] && eff[i] !== 100 - pcts[i] && eff[i] !== pcts[j];
        }
        if (!ok) { total = 200; pcts = [10, 25, 30, 20, 10, 5].slice(0, nbCl); eff = pcts.map(p => (total * p) / 100); i = 2; j = nbCl - 1; }
        const cible = discret ? `de la catégorie « ${ctx.cats[i]} »` : `de la classe ${libClasse(ctx, i)}`;
        const chBonne = `${pcts[i]} %`;
        const chEffectif = `${eff[i]} %`;                  // recopie l'effectif (effectif ≠ fréquence)
        const chComp = `${100 - pcts[i]} %`;               // complément (toutes les AUTRES classes)
        const chVoisin = `${pcts[j]} %`;                   // fréquence d'une autre classe (mauvaise lecture)
        let texte; let figure = null;
        if (support === 'tableau') {
            texte = `<p>On a étudié ${ctx.sujet.replace('X', String(total))} :</p>`
                + tableEnonce(
                    ligneTable('Classe', pcts.map((_, k) => libClasse(ctx, k))),
                    ligneTable('Effectif', eff.map(String)),
                )
                + `<p><b>Quelle est la fréquence ${cible} ?</b></p>`;
        } else if (support === 'batons') {
            texte = `<p>Le diagramme en bâtons représente ${ctx.sujet.replace('X', String(total))}.</p>`
                // « Lis l'effectif sur le diagramme, puis calcule » découpait le
                // travail : la lecture du support EST la première étape évaluée.
                + `<p><b>Quelle est la fréquence ${cible} ?</b></p>`;
            figure = injecteDesc215(
                svgBarres(ctx.cats, eff, { yStep: 10, yLabel: 'Effectifs' }),
                `calc|batons|${ctx.cats.join(';')}|${eff.join(',')}`,
            );
        } else if (support === 'circulaire') {
            texte = `<p>Le diagramme circulaire représente ${ctx.sujet.replace('X', String(total))} : la légende donne l'effectif de chaque catégorie.</p>`
                + `<p><b>Quelle est la fréquence ${cible} ?</b></p>`;
            figure = injecteDesc215(
                svgCamembert(
                    ctx.cats.map((c, k) => ({ nom: c, pct: pcts[k], etiquette: `${eff[k]}` })),
                    { sousTitre: `${total} réponses` },
                ),
                `calc|circulaire|${ctx.cats.join(';')}|${eff.join(',')}`,
            );
        } else {
            texte = `<p>L'histogramme représente ${ctx.sujet.replace('X', String(total))}.</p>`
                + `<p><b>Quelle est la fréquence ${cible} ?</b></p>`;
            figure = svgHisto(ctx.lo0, ctx.amp, eff, { yStep: ctx.yStep, xLabel: ctx.unite });
        }
        const payload = makeQcm([chBonne, chEffectif, chComp, chVoisin], chBonne, texte, opts(L_ETENDUE));
        if (figure) payload.elements_additionnels = imgElement(figure);
        payload._meta.params = { variante, support, total, pcts, eff, i, j };
        return payload;
    }
    // Effectif manquant : une donnée est masquée, l'effectif total est donné.
    const support = randomChoice(['tableau', 'histo']);
    const ctx = randomChoice(CONTEXTES);
    let total; let pcts; let eff; let iM; let autres; let unif; let ok = false;
    for (let essai = 0; essai < 80 && !ok; essai++) {
        const t = randomChoice([120, 160, 200, 240]);   // const d'itération (no-loop-func)
        total = t;
        pcts = tirerPcts(5);
        if (!pcts.every(p => (t * p) % 100 === 0)) continue;
        eff = pcts.map(p => (t * p) / 100);
        iM = randInt(0, 4);
        autres = total - eff[iM];                          // somme des données visibles
        unif = total / 5;                                  // « réparti pareil partout »
        ok = eff[iM] !== autres && eff[iM] !== unif && eff[iM] !== pcts[iM]
            && autres !== unif && autres !== pcts[iM] && unif !== pcts[iM];
    }
    if (!ok) { total = 200; pcts = [10, 25, 30, 20, 15]; eff = [20, 50, 60, 40, 30]; iM = 2; autres = 140; unif = 40; }
    const chBonne = String(eff[iM]);
    const chAutres = String(autres);                       // oubli de soustraire du total
    const chUnif = String(unif);                           // total ÷ nombre de classes
    const chPct = String(pcts[iM]);                        // confond fréquence (%) et effectif
    let texte; let figure = null;
    if (support === 'tableau') {
        texte = `<p>On a étudié ${ctx.sujet.replace('X', String(total))}. Une valeur du tableau a été effacée :</p>`
            + tableEnonce(
                ligneTable('Classe', pcts.map((_, k) => libClasse(ctx, k))),
                ligneTable('Effectif', eff.map((e, k) => (k === iM ? '?' : String(e)))),
            )
            + `<p><b>Calcule l'effectif de la classe ${libClasse(ctx, iM)}.</b> `
            + `(L'effectif total vaut ${total}.)</p>`;
    } else {
        texte = `<p>On a étudié ${ctx.sujet.replace('X', String(total))}. L'histogramme donne les effectifs, `
            + `mais la barre de la classe ${libClasse(ctx, iM)} a été effacée.</p>`
            + `<p><b>Calcule l'effectif de la classe ${libClasse(ctx, iM)}.</b> `
            + `(L'effectif total vaut ${total}.)</p>`;
        figure = svgHisto(ctx.lo0, ctx.amp, eff, { yStep: ctx.yStep, xLabel: ctx.unite, manquantIdx: iM });
    }
    const payload = makeQcm([chBonne, chAutres, chUnif, chPct], chBonne, texte, opts(L_ETENDUE));
    if (figure) payload.elements_additionnels = imgElement(figure);
    payload._meta.params = { variante, support, total, pcts, eff, iM };
    return payload;
};

// ═════════ « journal » — l'affirmation est-elle exacte ? (QCM) ═════════

const genJournalQcm = () => {
    const ctx = randomChoice(CONTEXTES);
    const total = randomChoice([100, 200]);
    const pcts = tirerPcts(6);
    const eff = pcts.map(p => (total * p) / 100);
    const k = randInt(2, 3);                               // « au moins lo0 + k·amp »
    const seuil = ctx.lo0 + k * ctx.amp;
    const pctVrai = somme(pcts.slice(k));
    const exact = randInt(0, 1) === 1;
    const deltas = [-10, -5, 5, 10].filter(d => pctVrai + d >= 5 && pctVrai + d <= 95);
    const pctTitre = exact ? pctVrai : pctVrai + randomChoice(deltas);
    const chVrai = `Le titre est exact : le calcul donne bien ${pctVrai} %.`;
    const chFaux = `Le titre est faux : le vrai pourcentage est ${pctVrai} %.`;
    const chImp = 'On ne peut pas calculer de pourcentage à partir d\'un histogramme.';
    const correct = exact ? chVrai : chFaux;
    const payload = makeQcm([chVrai, chFaux, chImp], correct,
        `<p>On a étudié ${ctx.sujet.replace('X', String(total))} :</p>`
        + tableEnonce(
            ligneTable('Classe', pcts.map((_, i) => libClasse(ctx, i))),
            ligneTable('Effectif', eff.map(String)),
        )
        + `<p>Un journal titre : « ${pctTitre} % dépassent ${seuil} ${ctx.unite} ».</p>`
        // « Calcule le pourcentage des valeurs d'au moins S » disait quel calcul
        // faire : décider ce qu'il faut calculer EST la lecture critique visée.
        + '<p><b>Ce titre est-il exact ?</b></p>',
        opts(L_JOURNAL));
    payload._meta.params = { total, pcts, k, seuil, pctVrai, pctTitre, exact };
    return payload;
};

// ─── Registre ────────────────────────────────────────────────
const GENERATORS = {
    frequences_texte_trou_saisie: genFrequencesTrous,
    effectifs_texte_trou_saisie: genEffectifsTrous,
    etendue_qcm_unique: genCalculQcm,
    journal_qcm_unique: genJournalQcm,
};

const FORMAT_LABELS = { texte_trou_saisie: 'Saisie trous', qcm_unique: 'QCM' };
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'frequences', label: L_FREQUENCES, formatsList: buildFormats('frequences') },
    { id: 'effectifs', label: L_EFFECTIFS, formatsList: buildFormats('effectifs') },
    { id: 'etendue', label: L_ETENDUE, formatsList: buildFormats('etendue') },
    { id: 'journal', label: L_JOURNAL, formatsList: buildFormats('journal') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
